import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Wand2, ExternalLink, Building2, CheckCircle2 } from 'lucide-react';

// dev-builder-200 Fase 3: picker por DESARROLLO (1 landing por desarrollo,
// ver routes/dev_builder.py). Se entra eligiendo el desarrollo, no tecleando
// un slug -- "Crear landing" ya sabe a qué desarrollo va.

export default function DevBuilderListPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const navigate = useNavigate();

  const [developments, setDevelopments] = useState([]);
  const [landingByDev, setLandingByDev] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [creatingFor, setCreatingFor] = useState(null);

  const load = async () => {
    try {
      setIsLoading(true);
      const [devsRes, landingsRes] = await Promise.all([
        api.get('/dev-builder/data/developments'),
        api.get('/dev-builder/landings'),
      ]);
      setDevelopments(devsRes.data.developments || []);
      const map = {};
      (landingsRes.data.landings || []).forEach((l) => { map[l.development_id] = l; });
      setLandingByDev(map);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cargando desarrollos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleClick = async (dev) => {
    const existing = landingByDev[dev.id];
    if (existing) {
      navigate(`/dev-builder/${existing.slug}`);
      return;
    }
    try {
      setCreatingFor(dev.id);
      const { data } = await api.post('/dev-builder/landings', { development_id: dev.id });
      navigate(`/dev-builder/${data.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando la landing');
    } finally {
      setCreatingFor(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Wand2 className="h-6 w-6" /> Dev Builder</h1>
        <p className="text-sm text-gray-600">Elige un desarrollo para crear o editar su landing pública</p>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-500 py-12">Cargando...</div>
      ) : developments.length === 0 ? (
        <div className="text-center text-gray-500 py-12 border border-dashed rounded-lg">
          No hay desarrollos activos todavía. Créalos primero en el módulo Desarrollos.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {developments.map((dev) => {
            const landing = landingByDev[dev.id];
            const isCreating = creatingFor === dev.id;
            const logoUrl = landing?.proyectos?.logo_url || dev.logo_url;
            return (
              <div key={dev.id} className="border border-gray-200 rounded-lg bg-white p-4 flex flex-col">
                <div className="h-16 flex items-center gap-2 mb-3">
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="h-full object-contain" />
                  ) : (
                    <Building2 className="h-10 w-10 text-gray-300" />
                  )}
                </div>
                <h3 className="font-semibold text-black truncate">{dev.name}</h3>
                <div className="mb-4 mt-1">
                  {landing?.is_published ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                      <CheckCircle2 className="h-3 w-3" /> Publicada
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">Sin landing</span>
                  )}
                </div>
                <div className="mt-auto flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-blue-600 border-blue-600 hover:bg-blue-100 hover:text-blue-600"
                    disabled={isCreating || (!landing && !isAdmin)}
                    onClick={() => handleClick(dev)}
                  >
                    {isCreating ? 'Creando...' : landing ? 'Editar' : 'Crear landing'}
                  </Button>
                  {landing?.is_published && (
                    <a href={`/dev-sites/${landing.slug}`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="text-black">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
